import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Typography,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAuth } from 'shared/contexts/AuthContext';
import { userApi } from 'shared/services/userApi';
import { incidentReportApi, IncidentReport } from 'shared/services/incidentReportApi';
import { getRoleDisplayName } from 'shared/utils/roleUtils';
import { UserRole } from 'shared/types';

const imageFileName = (key: string): string => key.split('/').pop() || key;

interface UserOption {
  _id: string;
  name: string;
  email?: string;
  role: string;
}

// A person involved can be a registered User (picked from the dropdown) or
// just a typed name — someone on site without an account (contractor,
// bystander, etc.), same as GuestVisit's optional `registeredUser`.
type PersonEntry = UserOption | string;

const entryName = (entry: PersonEntry): string => (typeof entry === 'string' ? entry : entry.name);
const entryKey = (entry: PersonEntry, index: number): string =>
  typeof entry === 'string' ? `custom-${index}-${entry}` : entry._id;

interface IncidentReportModalProps {
  open: boolean;
  onClose: () => void;
  companyId?: string;
  editingReport?: IncidentReport | null;
}

const toLocalInputValue = (date: Date): string => {
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
};

export const IncidentReportModal: React.FC<IncidentReportModalProps> = ({ open, onClose, companyId: propCompanyId, editingReport }) => {
  const { user } = useAuth();
  const isEditing = !!editingReport;

  const [description, setDescription] = useState('');
  const [occurredAt, setOccurredAt] = useState('');
  const [selectedEntries, setSelectedEntries] = useState<PersonEntry[]>([]);
  // Tracks the "Personas involucradas" text field so a name typed but never
  // committed with Enter isn't silently lost when the user clicks away —
  // onBlur below commits whatever's left. A ref (not just the state) backs
  // the read in onBlur so it sees the just-reset '' from an Enter/selection
  // that fired in the same interaction, rather than a stale pre-commit value.
  const [peopleInputValue, setPeopleInputValue] = useState('');
  const peopleInputRef = useRef('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [companyUsers, setCompanyUsers] = useState<UserOption[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Set default date/time to now, or prefill from the record being edited
  useEffect(() => {
    if (open) {
      if (editingReport) {
        setOccurredAt(toLocalInputValue(new Date(editingReport.occurredAt)));
        setDescription(editingReport.description);
        setSelectedEntries(
          editingReport.peopleInvolved.map((p) =>
            p.user
              ? {
                  _id: typeof p.user === 'object' ? p.user._id : p.user,
                  name: p.name,
                  email: typeof p.user === 'object' ? p.user.email : '',
                  role: p.role || '',
                }
              : p.name,
          ),
        );
        setExistingImages(editingReport.images || []);
      } else {
        setOccurredAt(toLocalInputValue(new Date()));
        setDescription('');
        setSelectedEntries([]);
        setExistingImages([]);
      }
      setPeopleInputValue('');
      peopleInputRef.current = '';
      setAttachments([]);
      setError(null);
      setSuccess(false);
    }
  }, [open, editingReport]);

  // Resolve company ID from prop or user context
  const userCompany = user?.company;
  const resolvedCompanyId = propCompanyId
    || (userCompany && typeof userCompany === 'object' ? userCompany._id : userCompany)
    || undefined;

  // Load company users
  useEffect(() => {
    if (!open || !resolvedCompanyId) return;

    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const res = await userApi.getSimple({ company: resolvedCompanyId, limit: 100 });
        const users = (res?.data || []).map((u) => ({
          _id: u._id,
          name: [u.firstName, u.lastName].filter(Boolean).join(' ') || 'Sin nombre',
          email: u.email,
          role: u.role || '',
        }));
        setCompanyUsers(users);
      } catch {
        // Silently fail — dropdown will just be empty
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, [open, resolvedCompanyId]);

  const totalImageCount = existingImages.length + attachments.length;

  const handleFileAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setAttachments((prev) => [...prev, ...newFiles].slice(0, 5 - existingImages.length));
    }
  };

  const handleRemoveFile = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = (key: string) => {
    setExistingImages((prev) => prev.filter((k) => k !== key));
  };

  const handleViewExistingImage = async (key: string) => {
    if (!editingReport) return;
    const index = editingReport.images.indexOf(key);
    if (index === -1) return;
    try {
      const { url } = await incidentReportApi.getImageUrl(editingReport._id, index);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setError('No se pudo abrir el archivo adjunto');
    }
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      setError('La descripción es requerida');
      return;
    }
    if (!occurredAt) {
      setError('La fecha es requerida');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const peopleInvolved = selectedEntries
        .map((entry) =>
          typeof entry === 'string'
            ? { name: entry.trim() }
            : { user: entry._id, name: entry.name, role: entry.role },
        )
        .filter((p) => p.name);

      if (isEditing && editingReport) {
        await incidentReportApi.update(editingReport._id, {
          description: description.trim(),
          occurredAt: new Date(occurredAt).toISOString(),
          peopleInvolved,
          keepImages: existingImages,
          images: attachments.length > 0 ? attachments : undefined,
        });
      } else {
        await incidentReportApi.create({
          description: description.trim(),
          occurredAt: new Date(occurredAt).toISOString(),
          companyId: resolvedCompanyId,
          peopleInvolved,
          images: attachments.length > 0 ? attachments : undefined,
        });
      }
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || `Error al ${isEditing ? 'guardar los cambios' : 'enviar el reporte'}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate min date (24 hours ago)
  const minDate = () => {
    const d = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  };

  const maxDate = () => {
    const d = new Date();
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box component="span" sx={{ fontWeight: 600 }}>
          {isEditing ? 'Editar Reporte de Accidente' : 'Reporte de Accidente'}
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Cerrar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {success ? (
          <Alert severity="success" sx={{ my: 2 }}>
            {isEditing ? 'Reporte actualizado exitosamente' : 'Reporte enviado exitosamente'}
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            {/* Description */}
            <TextField
              label="Descripción del incidente"
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              fullWidth
              placeholder="Describa qué ocurrió..."
            />

            {/* Date & Time */}
            <TextField
              label="Fecha y hora del incidente"
              type="datetime-local"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              required
              fullWidth
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { min: minDate(), max: maxDate() },
              }}
              helperText="No puede ser más de 24 horas en el pasado"
            />

            {/* People Involved — either a registered User picked from the
                dropdown, or a free-typed name for someone without an
                account (contractor, visitor, bystander, etc.). */}
            <Autocomplete<UserOption, true, false, true>
              multiple
              freeSolo
              options={companyUsers.filter(
                (u) => !selectedEntries.some((s) => typeof s !== 'string' && s._id === u._id),
              )}
              getOptionLabel={(opt) => entryName(opt)}
              isOptionEqualToValue={(opt, val) =>
                typeof val !== 'string' && opt._id === val._id
              }
              value={selectedEntries}
              onChange={(_, val) => {
                const cleaned = val
                  .map((entry) => (typeof entry === 'string' ? entry.trim() : entry))
                  .filter((entry) => entryName(entry));
                const seen = new Set<string>();
                const deduped = cleaned.filter((entry) => {
                  const key = entryName(entry).toLowerCase();
                  if (seen.has(key)) return false;
                  seen.add(key);
                  return true;
                });
                setSelectedEntries(deduped);
              }}
              inputValue={peopleInputValue}
              onInputChange={(_, newInputValue) => {
                peopleInputRef.current = newInputValue;
                setPeopleInputValue(newInputValue);
              }}
              onBlur={() => {
                const trimmed = peopleInputRef.current.trim();
                if (trimmed) {
                  setSelectedEntries((prev) => {
                    const isDuplicate = prev.some(
                      (entry) => entryName(entry).toLowerCase() === trimmed.toLowerCase(),
                    );
                    return isDuplicate ? prev : [...prev, trimmed];
                  });
                  peopleInputRef.current = '';
                  setPeopleInputValue('');
                }
              }}
              loading={loadingUsers}
              renderOption={(props, opt) => (
                <li {...props} key={opt._id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                      {opt.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 1, minWidth: 0 }}>
                      {opt.email}
                    </Typography>
                    <Chip label={getRoleDisplayName(opt.role as UserRole)} size="small" variant="outlined" sx={{ ml: 'auto', height: 20, fontSize: '0.65rem', flexShrink: 0 }} />
                  </Box>
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((opt, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={entryKey(opt, index)}
                    label={entryName(opt)}
                    size="small"
                    color={typeof opt === 'string' ? 'default' : 'primary'}
                    variant={typeof opt === 'string' ? 'outlined' : 'filled'}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Personas involucradas"
                  placeholder="Buscar usuario o escribir un nombre..."
                  helperText="Opcional — busque un usuario registrado o escriba el nombre de cualquier persona y presione Enter"
                />
              )}
            />

            {/* Attachments (images + PDFs) */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Archivos adjuntos (opcional, máx. 5 — imágenes o PDF)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {existingImages.map((key) => (
                  <Chip
                    key={key}
                    label={imageFileName(key)}
                    size="small"
                    variant="outlined"
                    onClick={() => handleViewExistingImage(key)}
                    onDelete={() => handleRemoveExistingImage(key)}
                    deleteIcon={<DeleteIcon fontSize="small" />}
                  />
                ))}
                {attachments.map((file, i) => (
                  <Chip
                    key={i}
                    label={file.name.length > 20 ? file.name.slice(0, 20) + '...' : file.name}
                    size="small"
                    onDelete={() => handleRemoveFile(i)}
                    deleteIcon={<DeleteIcon fontSize="small" />}
                  />
                ))}
                {totalImageCount < 5 && (
                  <Button
                    component="label"
                    size="small"
                    variant="outlined"
                    startIcon={<AttachFileIcon />}
                  >
                    Agregar
                    <input
                      type="file"
                      hidden
                      aria-label="Seleccionar archivo adjunto"
                      accept="image/*,application/pdf"
                      multiple
                      onChange={handleFileAdd}
                    />
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="inherit">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="error"
            disabled={submitting || !description.trim()}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {submitting ? (isEditing ? 'Guardando...' : 'Enviando...') : (isEditing ? 'Guardar Cambios' : 'Reportar Accidente')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
